import { useState, useEffect, useCallback, useMemo } from 'react';
import { ref, onValue, push, update, remove } from 'firebase/database';
import { auth, database } from '../firebase';

/**
 * Hook for managing liabilities and IOUs.
 * Reads from flo/liabilities and flo/ious in Firebase.
 */
const useDebtData = () => {
  const [liabilities, setLiabilities] = useState([]);
  const [ious, setIOUs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Firebase listeners
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        setLiabilities([]);
        setIOUs([]);
        setLoading(false);
        return;
      }

      const userId = user.uid;

      // Liabilities listener
      const liabilitiesRef = ref(database, `users/${userId}/flo/liabilities`);
      const unsubLiabilities = onValue(liabilitiesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const parsed = Object.entries(data).map(([id, val]) => ({ id, ...val }));
          parsed.sort((a, b) => (b.currentBalance || 0) - (a.currentBalance || 0));
          setLiabilities(parsed);
        } else {
          setLiabilities([]);
        }
        setLoading(false);
      });

      // IOUs listener
      const iousRef = ref(database, `users/${userId}/flo/ious`);
      const unsubIOUs = onValue(iousRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const parsed = Object.entries(data).map(([id, val]) => ({ id, ...val }));
          parsed.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
          setIOUs(parsed);
        } else {
          setIOUs([]);
        }
      });

      return () => {
        unsubLiabilities();
        unsubIOUs();
      };
    });

    return () => unsubscribe();
  }, []);

  // ── Liability CRUD ──

  const addLiability = useCallback(async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const now = new Date().toISOString();
    const liabRef = ref(database, `users/${user.uid}/flo/liabilities`);
    await push(liabRef, { ...data, createdAt: now, updatedAt: now });
  }, []);

  const updateLiability = useCallback(async (id, updates) => {
    const user = auth.currentUser;
    if (!user) return;
    const liabRef = ref(database, `users/${user.uid}/flo/liabilities/${id}`);
    await update(liabRef, { ...updates, updatedAt: new Date().toISOString() });
  }, []);

  const deleteLiability = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    await remove(ref(database, `users/${user.uid}/flo/liabilities/${id}`));
  }, []);

  // ── IOU CRUD ──

  const addIOU = useCallback(async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const now = new Date().toISOString();
    const iousRef = ref(database, `users/${user.uid}/flo/ious`);
    await push(iousRef, {
      ...data,
      status: 'active',
      settledDate: null,
      createdAt: now,
      updatedAt: now,
    });
  }, []);

  const updateIOU = useCallback(async (id, updates) => {
    const user = auth.currentUser;
    if (!user) return;
    const iouRef = ref(database, `users/${user.uid}/flo/ious/${id}`);
    await update(iouRef, { ...updates, updatedAt: new Date().toISOString() });
  }, []);

  const settleIOU = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    const iouRef = ref(database, `users/${user.uid}/flo/ious/${id}`);
    await update(iouRef, {
      status: 'settled',
      settledDate: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString(),
    });
  }, []);

  const unsettleIOU = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    const iouRef = ref(database, `users/${user.uid}/flo/ious/${id}`);
    await update(iouRef, {
      status: 'active',
      settledDate: null,
      updatedAt: new Date().toISOString(),
    });
  }, []);

  const deleteIOU = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    await remove(ref(database, `users/${user.uid}/flo/ious/${id}`));
  }, []);

  // ── Derived data ──

  const activeIOUs = useMemo(() => ious.filter((i) => i.status === 'active'), [ious]);
  const settledIOUs = useMemo(() => ious.filter((i) => i.status === 'settled'), [ious]);
  const owedToMe = useMemo(() => activeIOUs.filter((i) => i.direction === 'owed_to_me'), [activeIOUs]);
  const iOwe = useMemo(() => activeIOUs.filter((i) => i.direction === 'i_owe'), [activeIOUs]);

  const totalDebt = useMemo(
    () => liabilities.reduce((sum, l) => sum + (l.currentBalance || 0), 0),
    [liabilities]
  );

  const totalMinPayment = useMemo(
    () => liabilities.reduce((sum, l) => sum + (l.monthlyPayment || 0), 0),
    [liabilities]
  );

  const netIOUPosition = useMemo(() => {
    const totalOwed = owedToMe.reduce((sum, i) => sum + (i.amount || 0), 0);
    const totalIOwe = iOwe.reduce((sum, i) => sum + (i.amount || 0), 0);
    return totalOwed - totalIOwe;
  }, [owedToMe, iOwe]);

  return {
    liabilities,
    ious,
    loading,
    // Liability operations
    addLiability,
    updateLiability,
    deleteLiability,
    // IOU operations
    addIOU,
    updateIOU,
    settleIOU,
    unsettleIOU,
    deleteIOU,
    // Derived
    activeIOUs,
    settledIOUs,
    owedToMe,
    iOwe,
    totalDebt,
    totalMinPayment,
    netIOUPosition,
  };
};

export default useDebtData;
